/**
 * Master Data Contact API Types
 * Types for standalone contact management endpoints (under Master Data)
 * Contacts can be associated with either vendors or customers
 */

import type { ListResponse, PaginationParams } from './common';

/**
 * Contact type enum (numeric values as per API spec)
 * 1 = Vendor contact
 * 2 = Customer contact
 */
export type ContactType = 1 | 2;

/**
 * Affiliation company side (#92 contact identity)
 */
export type AffiliationCompanyType = 'vendor' | 'customer';

/**
 * One company affiliation of a master-data contact (#92).
 *
 * A contact is ONE person who may be linked to several companies at once
 * (vendor A + vendor B, or a vendor + a customer). `isPrimary` marks the
 * person's principal affiliation (the one legacy `vendorId`/`customerId`
 * single-org fields mirror) — it is NOT the per-company "primary contact"
 * flag, which stays on the contact itself.
 */
export interface ContactAffiliation {
  companyType: AffiliationCompanyType;
  companyId: number;
  companyName: string;
  roleTitle?: string | null;
  isPrimary?: boolean;
}

/**
 * Affiliation as sent on create/update requests (#92).
 * `companyName` is optional — the backend resolves it from `companyId`.
 */
export interface ContactAffiliationInput {
  companyType: AffiliationCompanyType;
  companyId: number;
  companyName?: string;
  roleTitle?: string | null;
  isPrimary?: boolean;
}

/**
 * Backward-compatible read (#92): legacy single-org records carry only
 * `vendorId` XOR `customerId` (+ `companyName`, `jobTitle`) and no
 * `affiliations` array. This transform maps such a record to a one-element
 * affiliation list; records already carrying a non-empty list pass through
 * unchanged. Records with neither (e.g. the masters live-GQL leg, which
 * cannot distinguish the party side yet) normalize to an empty list.
 */
export function normalizeContactAffiliations(record: {
  affiliations?: ContactAffiliation[] | null;
  vendorId?: number | null;
  customerId?: number | null;
  companyName?: string | null;
  jobTitle?: string | null;
}): ContactAffiliation[] {
  if (record.affiliations && record.affiliations.length > 0) {
    return record.affiliations;
  }
  if (record.vendorId) {
    return [
      {
        companyType: 'vendor',
        companyId: record.vendorId,
        companyName: record.companyName ?? '',
        roleTitle: record.jobTitle ?? null,
        isPrimary: true,
      },
    ];
  }
  if (record.customerId) {
    return [
      {
        companyType: 'customer',
        companyId: record.customerId,
        companyName: record.companyName ?? '',
        roleTitle: record.jobTitle ?? null,
        isPrimary: true,
      },
    ];
  }
  return [];
}

/**
 * Contact type labels
 */
export const ContactTypeLabels = {
  1: 'Vendor',
  2: 'Customer',
} as const;

/**
 * Master Data Contact list item (from /api/contacts)
 * GET /api/contacts response item
 */
export interface MasterDataContactListItem {
  contactId: number;
  name: string;
  jobTitle: string;
  companyName: string;
  phoneNumber: string; // Formatted: "+1 307-555-0133"
  mobile: string; // Formatted: "+1 307-555-0133"
  fax: string | null;
  email: string;
  // Address fields - using IDs for full address display
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
  postcode: string | null;
  address: string | null;
  addressLang: string | null;
  // Optional pre-resolved location names (for display and edit mode fallback)
  country?: string | null;
  state?: string | null;
  city?: string | null;
  isPrimary: boolean;
  isActive: boolean;
  // Legacy single-org fields — mirror the PRIMARY affiliation (#92)
  vendorId: number | null; // Present if primary affiliation is a vendor
  customerId: number | null; // Present if primary affiliation is a customer
  /**
   * Multi-affiliation list (#92): every company this person is linked to.
   * Optional on the wire (legacy rows omit it); the service transform layer
   * guarantees it via normalizeContactAffiliations().
   */
  affiliations?: ContactAffiliation[];
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Master Data Contact list response
 * GET /api/contacts
 */
export type MasterDataContactListResponse = ListResponse<MasterDataContactListItem>;

/**
 * Master Data Contact detail (single contact)
 * Matches vendor/customer contact detail response
 */
export interface MasterDataContactDetail {
  contactId: number;
  isPrimary: boolean;
  name: string;
  jobTitle: string;
  companyName: string | null;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension: string | null;
  mobileCountryCode: string | null;
  mobileNumber: string | null;
  fax: string | null;
  email: string;
  // Address fields - using IDs
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
  postcode: string | null;
  address: string | null;
  addressLang: string | null; // Address language (en/zh)
  // Optional pre-resolved location names (for edit mode fallback)
  country?: string | null;
  state?: string | null;
  city?: string | null;
  isActive: boolean;
  // Type info — legacy single-org fields mirror the PRIMARY affiliation (#92)
  vendorId: number | null;
  customerId: number | null;
  /**
   * Multi-affiliation list (#92). Optional on the wire (legacy records omit
   * it); the service transform layer guarantees it via
   * normalizeContactAffiliations().
   */
  affiliations?: ContactAffiliation[];
  // Timestamps
  createdAt?: string;
  updatedAt?: string;
}

/**
 * Master Data Contact sort fields (numeric as per API spec)
 * 1=name, 2=jobTitle, 3=companyName, 4=phone, 5=email, 6=isPrimary, 7=isActive
 */
export type MasterDataContactSortField = 1 | 2 | 3 | 4 | 5 | 6 | 7;

/**
 * Master Data Contact list query parameters
 * GET /api/contacts
 */
export interface MasterDataContactListParams extends PaginationParams {
  search?: string; // Search by company name or contact name
  typeId: ContactType; // Required: 1=Vendor, 2=Customer
  isPrimary?: number; // 1=primary only, 2=general only, 3=all (default: 3)
  isActive?: number; // 1=active, 2=inactive, 3=all (default: 3)
  sortBy?: MasterDataContactSortField;
  sortOrder?: 0 | 1; // 0=ASC, 1=DESC
}

/**
 * Create vendor contact request
 * POST /api/vendors/:vendorId/contacts
 */
export interface CreateVendorContactRequest {
  vendorId: number;
  isPrimary?: boolean;
  name: string;
  jobTitle: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  fax?: string;
  email: string;
  countryId?: number;
  stateId?: number;
  cityId?: number;
  postcode?: string;
  address?: string;
  addressLang?: string;
  isActive?: boolean;
  /**
   * Multi-affiliation list (#92). Omitted = single affiliation derived from
   * the path entity (backward-compatible legacy create).
   */
  affiliations?: ContactAffiliationInput[];
}

/**
 * Create customer contact request
 * POST /api/customers/:customerId/contacts
 */
export interface CreateCustomerContactRequest {
  customerId: number;
  isPrimary?: boolean;
  name: string;
  jobTitle: string;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension?: string;
  mobileCountryCode?: string;
  mobileNumber?: string;
  fax?: string;
  email: string;
  countryId?: number;
  stateId?: number;
  cityId?: number;
  postcode?: string;
  address?: string;
  addressLang?: string;
  isActive?: boolean;
  /**
   * Multi-affiliation list (#92). Omitted = single affiliation derived from
   * the path entity (backward-compatible legacy create).
   */
  affiliations?: ContactAffiliationInput[];
}

/**
 * Update contact request (same for vendor/customer)
 */
export type UpdateMasterDataContactRequest = Omit<CreateVendorContactRequest, 'vendorId'>;

/**
 * Toggle contact status response
 */
export interface ToggleMasterDataContactResponse {
  contactId: number;
  isPrimary: boolean;
  name: string;
  jobTitle: string;
  companyName: string | null;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension: string | null;
  mobileCountryCode: string | null;
  mobileNumber: string | null;
  fax: string | null;
  email: string;
  address: string | null;
  isActive: boolean;
}

/**
 * Set primary contact response
 */
export interface SetPrimaryMasterDataContactResponse {
  contactId: number;
  isPrimary: boolean;
  name: string;
  jobTitle: string;
  companyName: string | null;
  phoneCountryCode: string;
  phoneNumber: string;
  phoneExtension: string | null;
  mobileCountryCode: string | null;
  mobileNumber: string | null;
  fax: string | null;
  email: string;
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
  postcode: string | null;
  address: string | null;
  addressLang: string | null;
  isActive: boolean;
}

/**
 * Vendor list item for dropdown (minimal fields)
 */
export interface VendorListItem {
  vendorId: number;
  vendorName: string;
  isActive: boolean;
}

/**
 * Customer list item for dropdown (minimal fields)
 */
export interface CustomerListItem {
  customerId: number;
  customerName: string;
  isActive: boolean;
}
